import React, { useMemo, useState } from 'react';
import { Alert, Button, Modal, Space, Steps, Typography, message } from 'antd';
import _ from 'lodash';
import {
  InternalTransferResult,
  OperationWallet,
  OperationWalletProvider,
} from '../../domain/usdt-providers/operationWalletProvider';
import { Environment } from '../../core/configs/services';

const { Step } = Steps;
const { Text } = Typography;

/** 內轉是非同步的:送出只是「受理」。跟 InternalTransferModal 用同一組節奏。 */
const POLL_MS = 3000;
const POLL_MAX = 120; // 6 分鐘

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function tronscanTxUrl(txId: string): string {
  const env = Environment() ?? 'develop';
  const base = _.find(['prod', 'stg'], (x) => x === env)
    ? 'https://tronscan.org/#/transaction/'
    : 'https://shasta.tronscan.org/#/transaction/';
  return base + txId;
}

const STEP_TITLES = ['建立新錢包', '加入運營錢包(停用)', '內轉(全部清空)', '啟用(出款+歸集)'];

type Props = {
  open: boolean;
  /** 要重生的來源錢包。必須是「已停用」的出款錢包 —— 呼叫端負責擋。 */
  source: OperationWallet | null;
  onClose: () => void;
  onDone: () => void;
};

/**
 * 重生:把一顆出款錢包換成全新的錢包,並把餘額搬過去。
 *
 * 三步各自是一支「短」的 API —— 不是一支做完三件事。單一 endpoint 跑完整套會在請求上坐太久,
 * 直接 504(內轉本身就可能要好幾分鐘)。所以由前端逐步驅動:
 *   1 建錢包(~1s) → 2 註冊成停用的出款錢包(<1s) → 3 內轉(立刻回受理,之後輪詢)
 *
 * 任何一步失敗就停在那一步,可以只重試那一步 —— 不會重跑前面的(新地址存在這個 state 裡,
 * 步驟1 成功後重試不會再鑄第二顆錢包)。
 */
const RebirthModal = (props: Props) => {
  const { open, source, onClose, onDone } = props;

  const [current, setCurrent] = useState(0);
  const [state, setState] = useState<'idle' | 'running' | 'failed' | 'done'>('idle');
  const [newAddress, setNewAddress] = useState<string | undefined>();
  const [err, setErr] = useState<string | undefined>();
  const [result, setResult] = useState<InternalTransferResult | null>(null);

  // 每次開啟產一次,連點兩下也只會轉一筆(後端用它去重)。
  const requestKey = useMemo(
    () => (source ? `rebirth-${source.address}-${Date.now()}` : ''),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [source, open],
  );

  const reset = () => {
    setCurrent(0);
    setState('idle');
    setNewAddress(undefined);
    setErr(undefined);
    setResult(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const errMsg = (e: any) =>
    e?.response?.data?.displayMessage || e?.response?.data?.message || e?.message || '失敗';

  /** 從第 start 步往下跑;失敗就停在那步。 */
  const runFrom = async (start: number) => {
    // addr 用區域變數帶著跑 —— setState 是非同步的,同一輪迴圈裡讀不到剛設的值。
    let addr = newAddress;

    for (let i = start; i < STEP_TITLES.length; i++) {
      setCurrent(i);
      setState('running');
      setErr(undefined);
      try {
        if (i === 0) {
          const r = await OperationWalletProvider.createVcWallet();
          addr = (r as any)?.data?.address;
          if (!addr) throw new Error('vc-wallet-service 沒有回傳新地址');
          setNewAddress(addr);
        } else if (i === 1) {
          await OperationWalletProvider.create({
            channel: source!.channel,
            address: addr!,
            type: 'PAYOUT',
            enabled: 0, // 一律停用 —— 內轉完成後由第 4 步自動啟用
            note: '出款錢包',
          });
        } else if (i === 2) {
          await OperationWalletProvider.internalTransfer({
            fromAddress: source!.address,
            toAddress: addr!,
            mode: 'sweep',
            requestKey,
          });
          let settled = false;
          for (let n = 0; n < POLL_MAX; n++) {
            await sleep(POLL_MS);
            const st = (await OperationWalletProvider.internalTransferStatus(requestKey)).data;
            if (st?.status === 'done') {
              setResult(st.result);
              settled = true;
              break;
            }
            if (st?.status === 'failed') throw new Error(st.error || '內轉失敗');
          }
          if (!settled) {
            throw new Error('內轉仍在處理中 —— 請稍後在列表確認餘額,或到 tronscan 查看');
          }
        } else if (i === 3) {
          // 內轉完成後自動啟用新出款錢包:出款(enabled)+ 歸集(collectible)都開。
          // 用 addr 查 id(不依賴 create 回傳,重試也不怕)。
          const list = (await OperationWalletProvider.list('TRC')).data || [];
          const w = (list as any[]).find((x) => x.address === addr);
          if (!w) {
            throw new Error('找不到新錢包,無法自動啟用 —— 請到列表手動啟用(出款+歸集)');
          }
          await OperationWalletProvider.update(w.id, { enabled: 1, collectible: 1 });
        }
      } catch (e: any) {
        setErr(errMsg(e));
        setState('failed');
        return; // 停在這步,等你按重試
      }
    }
    setState('done');
    onDone();
    message.success('重生完成 —— 新錢包已建立、收到餘額,並已啟用(出款+歸集)');
  };

  const stepStatus = state === 'failed' ? 'error' : state === 'done' ? 'finish' : 'process';

  return (
    <Modal
      title="運營錢包重生"
      visible={open}
      onCancel={close}
      maskClosable={state !== 'running'}
      closable={state !== 'running'}
      footer={
        <Space>
          {state === 'idle' && current === 0 && (
            <Button type="primary" onClick={() => runFrom(0)} disabled={!source}>
              開始重生
            </Button>
          )}
          {state === 'failed' && (
            <Button type="primary" danger onClick={() => runFrom(current)}>
              重試「{STEP_TITLES[current]}」
            </Button>
          )}
          <Button onClick={close} disabled={state === 'running'}>
            {state === 'done' ? '關閉' : '取消'}
          </Button>
        </Space>
      }
      destroyOnClose
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Text type="secondary">來源(將被清空)</Text>
          <div>
            <Text code>{source?.address}</Text>
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            USDT {Number(source?.balance ?? 0).toLocaleString()} / TRX{' '}
            {Number(source?.trxBalance ?? 0).toLocaleString()}
          </Text>
        </div>

        <Steps direction="vertical" size="small" current={current} status={stepStatus}>
          <Step
            title={STEP_TITLES[0]}
            description={
              newAddress ? <Text code>{newAddress}</Text> : '產生一顆全新的 TRON 錢包'
            }
          />
          <Step title={STEP_TITLES[1]} description="type=PAYOUT、停用、備註「出款錢包」" />
          <Step
            title={STEP_TITLES[2]}
            description="舊 → 新,先 USDT 再 TRX;這筆會順便把新地址開通(可能要幾分鐘)"
          />
          <Step
            title={STEP_TITLES[3]}
            description="內轉完成後,自動把新錢包設為可出款 + 可歸集"
          />
        </Steps>

        {err && <Alert type="error" showIcon message={`「${STEP_TITLES[current]}」失敗`} description={err} />}

        {state === 'running' && (
          <Alert
            type="info"
            showIcon
            message="處理中…"
            description="這個視窗先別關 —— 關掉不會中斷已送出的轉帳,但進度就看不到了。"
          />
        )}

        {state === 'done' && (
          <Alert
            type="success"
            showIcon
            message="重生完成"
            description={
              <Space direction="vertical" size={4}>
                <span>新錢包已建立並收到餘額,目前仍為「停用」—— 確認無誤後再手動啟用。</span>
                {result?.usdt && (
                  <a href={tronscanTxUrl(result.usdt.txId)} target="_blank" rel="noreferrer">
                    USDT {result.usdt.amount} — 交易
                  </a>
                )}
                {result?.trx && (
                  <a href={tronscanTxUrl(result.trx.txId)} target="_blank" rel="noreferrer">
                    TRX {result.trx.amount} — 交易
                  </a>
                )}
              </Space>
            }
          />
        )}
      </Space>
    </Modal>
  );
};

export default RebirthModal;
